import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  DialogContentText,
  IconButton,
  CircularProgress,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { IncommOfferForm } from 'steps/incomm-offer/components/incomm-offer-form/IncommOfferForm'
import { IncommOfferBarcodeResult } from 'steps/incomm-offer/components/IncommOfferBarcodeResult'
import { IncommOfferLayout } from 'steps/incomm-offer/components/IncommOfferLayout'
import { doGet } from 'services/common'
import closeLogo from 'images/icon-close.svg'

interface OfferData {
  id: number | string
  name: string
  description: string
  terms: string
  retailerIds: string[]
  availableStoreIds: string[]
  offerEnd?: string
  autoSendBarcodeEmail?: boolean
  // Customer View fields populated on the InComm offer itself.
  // When present, they take precedence over the legacy offer name/description
  // and act as the default presentation for any step or prize that doesn't
  // supply its own override. Prize-level overrides still beat these.
  customerTitle?: string | null
  customerDescription?: string | null
  bannerImageUrl?: string | null
  supportDescription?: string | null
  legalDescription?: string | null
  ctaText?: string | null
  checkboxes?: Array<{
    id: string
    label: string
    required: boolean
  }>
}

interface IncommOfferWinModalProps {
  open: boolean
  onClose: () => void
  incommOfferId: number | string
  step?: any
  // Optional prize-level display overrides
  offerTitle?: string | null
  offerDescription?: string | null
  bannerImageUrl?: string | null
  ctaText?: string | null
  // BannerStoreId values to restrict the store picker. Older payloads may send
  // this as CSV; admin forms may send it as an array before normalization.
  availableStoreIdsOverride?: string | string[] | null
  // Optional email to prefill in the form (e.g. from upstream leadgen submission)
  prefilledEmail?: string | null
  // BR-5706: name from an upstream form step, used to greet the user in the code email
  prefilledContact?: {
    firstName?: string | null
    lastName?: string | null
  } | null
}

export const IncommOfferWinModal = ({
  open,
  onClose,
  incommOfferId,
  step,
  offerTitle,
  offerDescription,
  bannerImageUrl,
  ctaText,
  availableStoreIdsOverride,
  prefilledEmail,
  prefilledContact,
}: IncommOfferWinModalProps) => {
  const [formStep, setFormStep] = useState<'form' | 'result'>('form')
  const [offerData, setOfferData] = useState<OfferData | null>(null)
  const [offerLoading, setOfferLoading] = useState(false)
  const [offerError, setOfferError] = useState<string | null>(null)
  const [barcodeResponse, setBarcodeResponse] = useState<any>(null)
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null)
  const [formError, setFormError] = useState<any>(null)
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)

  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  // On the form step the user has a live claimable offer. Closing there forfeits
  // the unique barcode, so we prompt for confirmation. On the result step the
  // barcode is already generated and safe to keep, so close is unguarded.
  const requestClose = () => {
    if (formStep === 'form') {
      setConfirmCloseOpen(true)
      return
    }
    onClose()
  }

  const confirmClose = () => {
    setConfirmCloseOpen(false)
    onClose()
  }

  useEffect(() => {
    if (open && incommOfferId) {
      setOfferLoading(true)
      setOfferError(null)
      doGet(`/incomm/offer/${incommOfferId}`)
        .then((response: any) => {
          setOfferData(response.detail)
        })
        .catch((err: any) => {
          console.error('Failed to load offer data for win modal:', err)
          setOfferError('Failed to load offer details. Please try again.')
        })
        .finally(() => {
          setOfferLoading(false)
        })
    }
  }, [open, incommOfferId])

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setFormStep('form')
      setBarcodeResponse(null)
      setSelectedStoreId(null)
      setFormError(null)
      setConfirmCloseOpen(false)
    }
  }, [open])

  const handleFormSubmit = async (data: any) => {
    // doPost2ErrorHandling returns { status, success, detail } — extract detail for barcode data
    const detail = data?.detail || data
    setBarcodeResponse(detail)
    setSelectedStoreId(data.selectedStoreId || detail.selectedStoreId || null)
    setFormStep('result')
  }

  const handleStatusModalOpen = (variant: string) => {
    if (variant === 'clip_limit_exceeded') {
      setFormError('You have already redeemed the maximum number of barcodes for this offer.')
    } else {
      setFormError('Something went wrong. Please try again.')
    }
  }

  // Resolve display values. Precedence (override-wins):
  //   1. Prize-level override (passed in from InstantWinController via prize.metadata)
  //   2. Offer-level Customer View (customerTitle / customerDescription / bannerImageUrl)
  //   3. Legacy offer fields (name, description) / no banner
  // Treat blank strings the same as null so an explicitly-cleared prize override
  // still falls through to the offer's Customer View.
  const firstNonBlank = (...values: Array<string | null | undefined>): string => {
    for (const v of values) {
      if (typeof v === 'string' && v.trim() !== '') return v
    }
    return ''
  }
  const resolvedTitle = firstNonBlank(offerTitle, offerData?.customerTitle, offerData?.name) || 'Claim Your Prize'
  const resolvedDescription = firstNonBlank(offerDescription, offerData?.customerDescription, offerData?.description)
  const resolvedBannerImageUrl = firstNonBlank(bannerImageUrl, offerData?.bannerImageUrl)
  const resolvedSupportDescription = firstNonBlank(offerData?.supportDescription)
  const resolvedTerms = firstNonBlank(offerData?.legalDescription, offerData?.terms)
  const resolvedCtaText = firstNonBlank(ctaText, offerData?.ctaText) || 'Get Offer'

  // Resolve store list with override-wins fallback (mirrors title/description chain):
  //   1. Prize-level override (comma-separated) — only if it parses to a non-empty list
  //   2. Offer-level Customer View / retailer-derived list from /incomm/offer/{id}
  //   3. Empty array → IncommOfferForm interprets this as "no restriction, show all stores"
  // A blank/whitespace/garbage override must not block fallback to the offer list.
  const parsedOverrideStoreIds: string[] = Array.isArray(availableStoreIdsOverride)
    ? availableStoreIdsOverride.map(s => String(s).trim()).filter(Boolean)
    : typeof availableStoreIdsOverride === 'string'
      ? availableStoreIdsOverride.split(',').map(s => s.trim()).filter(Boolean)
      : []
  const offerStoreIds: string[] = Array.isArray(offerData?.availableStoreIds)
    ? offerData!.availableStoreIds.filter(s => typeof s === 'string' && s.trim() !== '')
    : []
  const resolvedAvailableStoreIds: string[] =
    parsedOverrideStoreIds.length > 0 ? parsedOverrideStoreIds : offerStoreIds

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => reason !== 'backdropClick' && requestClose()}
      fullWidth
      fullScreen={isSmallScreen}
      maxWidth="sm"
      // Fully opaque backdrop so a parent modal's controls don't bleed through.
      BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.85)' } }}
      // Paper IS the visible card: beige background, rounded, clipped. This
      // keeps the scroll track and the close button inside the card rather
      // than floating outside of it.
      PaperProps={{
        sx: {
          background: '#F2EADA',
          borderRadius: isSmallScreen ? 0 : 2,
          overflow: 'hidden',
          maxHeight: isSmallScreen ? '100vh' : '90vh',
        },
      }}
    >
      {/* Close button — floated above the layout card */}
      <IconButton
        aria-label="close"
        onClick={requestClose}
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          background: 'rgba(255,255,255,0.85)',
          '&:hover': { background: 'rgba(255,255,255,1)' },
        }}
      >
        <img src={closeLogo} alt="Close" style={{ width: 20, height: 20 }} />
      </IconButton>

      <Dialog
        open={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>Leave without claiming?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            If you close now you'll lose this offer and won't be able to generate
            its barcode again. Are you sure you want to leave?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCloseOpen(false)} autoFocus>
            Keep offer
          </Button>
          <Button onClick={confirmClose} color="error">
            Leave anyway
          </Button>
        </DialogActions>
      </Dialog>

      <DialogContent sx={{ padding: 0, overflow: 'auto', flex: 1 }}>
        {offerLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <CircularProgress />
          </div>
        )}

        {offerError && !offerLoading && (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
            {offerError}
          </div>
        )}

        {!offerLoading && !offerError && offerData && (
          <IncommOfferLayout
            title={resolvedTitle}
            description={resolvedDescription}
            backgroundImageUrl={resolvedBannerImageUrl}
            showBanner={Boolean(resolvedBannerImageUrl || resolvedTitle || resolvedDescription)}
            supportDescription={resolvedSupportDescription}
            terms={resolvedTerms}
            // Paper already provides the card background; keep the inner layout
            // transparent so it doesn't paint a second beige card inside.
            cardBackgroundColor="transparent"
          >
            {formStep === 'form' && (
              <IncommOfferForm
                offer={{
                  id: String(offerData.id),
                  title: resolvedTitle,
                  description: resolvedDescription,
                  terms: resolvedTerms,
                  expirationDate: offerData.offerEnd || '',
                  amount: 0,
                  retailerIds: offerData.retailerIds || [],
                }}
                incommBarcodeForm={{
                  ctaLabel: resolvedCtaText,
                  availableStoreIds: resolvedAvailableStoreIds,
                  autoSendEmail: Boolean(offerData.autoSendBarcodeEmail),
                  defaultEmail: prefilledEmail || '',
                  defaultContact: prefilledContact || null,
                  checkboxes: Array.isArray(offerData.checkboxes) ? offerData.checkboxes : [],
                  formError,
                  onSubmit: handleFormSubmit,
                  onStatusModalOpen: handleStatusModalOpen,
                }}
              />
            )}

            {formStep === 'result' && barcodeResponse && (
              <IncommOfferBarcodeResult
                barcodeAccessToken={barcodeResponse.barcodeAccessToken}
                barcodeText={barcodeResponse.displayText || barcodeResponse.barcodeContent}
                offerEnd={offerData?.offerEnd}
                selectedStoreId={selectedStoreId || ''}
                email={barcodeResponse.email || prefilledEmail || undefined}
                offerId={incommOfferId}
                stepId={step?.originalStepId || step?.id}
                step={step}
                autoSendEmail={Boolean(offerData.autoSendBarcodeEmail)}
              />
            )}
          </IncommOfferLayout>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default IncommOfferWinModal
